import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { User, UserRole } from '@/types/api';
import { authService } from '@/services/authService';
import { 
  normalizeRole, 
  isAdmin as checkIsAdmin, 
  isAgent as checkIsAgent, 
  isEndUser as checkIsEndUser,
  hasMinimumRole,
  validateRoleData,
  debugRole
} from '@/utils/roleUtils';

// Auth State Interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

// Auth Actions
type AuthAction = 
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_INITIALIZE' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_PROFILE'; payload: User };

// Auth Context Interface
interface AuthContextType extends AuthState {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  isAgent: () => boolean;
  isEndUser: () => boolean;
}

// Registration data interface
interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      // Validate and normalize user role data
      const userData = action.payload;
      const roleValidation = validateRoleData(userData);
      
      if (!roleValidation.isValid) {
        console.warn('Role validation issues:', roleValidation.issues);
        debugRole(userData.role, 'AUTH_SUCCESS validation');
      }
      
      // Ensure user has normalized role
      const normalizedUser = {
        ...userData,
        role: roleValidation.normalizedRole
      };
      
      return {
        ...state,
        user: normalizedUser,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        isInitialized: true,
      };

    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        isInitialized: true,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        isInitialized: true,
      };

    case 'AUTH_INITIALIZE':
      return {
        ...state,
        isInitialized: true,
        isLoading: false,
      };

    case 'UPDATE_PROFILE':
      return {
        ...state,
        user: action.payload,
        error: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// Create auth context
const AuthContext = createContext<AuthContextType | null>(null);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on mount
  useEffect(() => {
    // Initialize authentication state
    const initializeAuth = async () => {
    try {
      console.log('Initializing auth...');
      dispatch({ type: 'AUTH_START' });

      // Check if user is already authenticated
      if (authService.isAuthenticated() && !authService.isTokenExpired()) {
        const user = authService.getCurrentUser();
        if (user) {
          console.log('User found in local storage:', user);
          
          // Debug role information from storage
          debugRole(user.role, 'Local storage user');
          
          dispatch({ type: 'AUTH_SUCCESS', payload: user });
          return;
        }
      }

      // Try to refresh token if we have a refresh token
      const refreshToken = authService.getRefreshToken();
      if (refreshToken && !authService.isTokenExpired()) {
        try {
          console.log('Attempting to refresh token...');
          const response = await authService.refreshToken(refreshToken);
          if (response.success) {
            // Get updated user profile
            const profileResponse = await authService.getProfile();
            if (profileResponse.success && profileResponse.data) {
              const user = profileResponse.data;
              
              // Debug role information during initialization
              debugRole(user.role, 'Profile refresh');
              
              dispatch({ type: 'AUTH_SUCCESS', payload: user });
              return;
            }
          }
        } catch (refreshError) {
          console.log('Token refresh failed, clearing auth:', refreshError);
          // Refresh failed, clear tokens
          await authService.logout();
        }
      }

      // No valid authentication, initialize as logged out
      console.log('No valid authentication found, initializing as logged out');
      dispatch({ type: 'AUTH_INITIALIZE' });
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Initialize anyway to show the app
      dispatch({ type: 'AUTH_INITIALIZE' });
    }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const response = await authService.login({ 
        email, 
        password, 
        rememberMe 
      });

      if (response.success && response.data) {
        const user = response.data.user;
        
        // Debug role information for troubleshooting
        debugRole(user.role, 'Login response');
        
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Register function
  const register = async (userData: RegisterData) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // Note: Backend should return user data after registration
      // For now, we'll assume registration requires separate login
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          role: 'EndUser', // Default role for registration
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const result = await response.json();
      
      // After successful registration, automatically log in
      await login(userData.email, userData.password);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  // Update profile function
  const updateProfile = async (updates: Partial<User>) => {
    try {
      const response = await authService.updateProfile(updates);
      
      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_PROFILE', payload: response.data });
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  // Forgot password function
  const forgotPassword = async (email: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Forgot password request failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Forgot password request failed';
      throw new Error(errorMessage);
    }
  };

  // Reset password function
  const resetPassword = async (token: string, newPassword: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Password reset failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      throw new Error(errorMessage);
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Enhanced role checking functions with normalization
  const hasRole = (role: UserRole): boolean => {
    if (!state.user) return false;
    const normalizedUserRole = normalizeRole(state.user.role);
    return normalizedUserRole === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!state.user) return false;
    const normalizedUserRole = normalizeRole(state.user.role);
    return roles.includes(normalizedUserRole);
  };

  const isAdmin = (): boolean => {
    return state.user ? checkIsAdmin(state.user.role) : false;
  };

  const isAgent = (): boolean => {
    return state.user ? checkIsAgent(state.user.role) : false;
  };

  const isEndUser = (): boolean => {
    return state.user ? checkIsEndUser(state.user.role) : false;
  };

  // Context value
  const value: AuthContextType = {
    ...state,
    login,
    logout,
    register,
    updateProfile,
    forgotPassword,
    resetPassword,
    clearError,
    hasRole,
    hasAnyRole,
    isAdmin,
    isAgent,
    isEndUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// HOC for components that require authentication
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  const AuthenticatedComponent = (props: P) => {
    const { isAuthenticated, isInitialized } = useAuth();

    if (!isInitialized) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return <div>Access denied. Please log in.</div>;
    }

    return <Component {...props} />;
  };

  AuthenticatedComponent.displayName = `withAuth(${Component.displayName || Component.name})`;
  return AuthenticatedComponent;
}

// HOC for role-based access control
export function withRole<P extends object>(
  Component: React.ComponentType<P>,
  allowedRoles: UserRole[]
): React.ComponentType<P> {
  const RoleProtectedComponent = (props: P) => {
    const { hasAnyRole, isAuthenticated, isInitialized } = useAuth();

    if (!isInitialized) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return <div>Access denied. Please log in.</div>;
    }

    if (!hasAnyRole(allowedRoles)) {
      return <div>Access denied. Insufficient permissions.</div>;
    }

    return <Component {...props} />;
  };

  RoleProtectedComponent.displayName = `withRole(${Component.displayName || Component.name})`;
  return RoleProtectedComponent;
}